import { useState, useEffect, useCallback } from "react";
import { createContainer } from "unstated-next";

import { createRequestToken, retrieveAccountId, retrieveSessionId } from "../api/tokens";
import { useFetch } from "../hooks/http-hook";

interface Token {
  requestToken: string;
  expirationDate: Date;
}

let logoutTimer: NodeJS.Timeout;

export const useToken = () => {
  const { sendRequest } = useFetch();
  const [requestToken, setRequestToken] = useState<string | null>(null);
  const [tokenExpirationDate, setTokenExpirationDate] = useState<Date | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);

  const getToken = useCallback(() => {
    createRequestToken(sendRequest)
      .then((response: any) => {
        setRequestToken(response.request_token);
        setTokenExpirationDate(new Date(response.expires_at));
        localStorage.setItem(
          "requestToken",
          JSON.stringify({ requestToken: response.request_token, expirationDate: response.expires_at })
        );
      })
      .catch((error: string) => console.log(error));
  }, [sendRequest]);

  const getSessionId = useCallback(() => {
    if (requestToken) {
      retrieveSessionId(sendRequest, requestToken)
        .then((response: any) => {
          if (response.success) {
            setSessionId(response.session_id);
            localStorage.setItem("sessionId", response.session_id);
          }
        })
        .catch((error: string) => console.log(error));
    }
  }, [sendRequest, requestToken]);

  const getAccountId = useCallback(() => {
    if (sessionId) {
      retrieveAccountId(sendRequest, sessionId)
        .then((response: any) => {
          setAccountId(response.id);
          localStorage.setItem("accountId", JSON.stringify(response.id));
        })
        .catch((error: string) => console.log(error));
    }
  }, [sendRequest, sessionId]);

  const getRedirectLink = (link: string) => {
    sessionStorage.setItem("redirectLink", link);
  };

  const login = (requestToken: string, expirationDate: Date, sessionId: string | null, accountId: string | null) => {
    setRequestToken(requestToken);
    setTokenExpirationDate(new Date(expirationDate));
    if (sessionId) {
      setSessionId(sessionId);
    }
    if (accountId) {
      setAccountId(accountId);
    }
  };

  const logout = () => {
    localStorage.removeItem("requestToken");
    localStorage.removeItem("sessionId");
    localStorage.removeItem("accountId");
    sessionStorage.removeItem("redirectLink");
    setRequestToken(null);
    setTokenExpirationDate(null);
    setSessionId(null);
    setAccountId(null);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("requestToken");
    const storedAccountId = localStorage.getItem("accountId");
    const token: Token | null = storedToken && JSON.parse(storedToken);
    const id: string = storedAccountId && JSON.parse(storedAccountId);
    const storedSessionId = localStorage.getItem("sessionId");

    if (token) {
      if (new Date(token.expirationDate) > new Date()) {
        login(token.requestToken, token.expirationDate, storedSessionId, id);
      } else {
        logout();
      }
    }
  }, []);

  useEffect(() => {
    if (requestToken && tokenExpirationDate) {
      const expiresIn = tokenExpirationDate.getTime() - new Date().getTime();
      logoutTimer = setTimeout(logout, expiresIn);
    } else {
      clearTimeout(logoutTimer);
    }
  }, [requestToken, tokenExpirationDate]);

  return {
    requestToken,
    tokenExpirationDate,
    sessionId,
    accountId,
    getToken,
    getSessionId,
    getAccountId,
    getRedirectLink,
    logout,
  };
};

const Tokens = createContainer(useToken);

export default Tokens;
